/* What time it was, in the project's timezone rather than the device's.
 *
 * Every deadline in this contract is a wall-clock time in America/Chicago.
 * The server computes them there, the proof-of-delivery PDF prints them
 * there, and the SLA report measures against them there. Until this module
 * existed the screens did not: they called toLocaleTimeString with no zone,
 * which means the zone the browser happens to be set to.
 *
 * On a courier's phone in San Antonio that is the same answer, which is
 * exactly why it survived so long. Anywhere else it is not: a delivery made
 * at 1:08 PM read "2:08 PM" on a laptop in Eastern time while the proof of
 * delivery for the same delivery, linked from the same page, said 1:08 PM.
 * Two answers to one question is how a billing dispute starts.
 *
 * So a formatter is built from a timezone and never from nothing. The zone
 * comes from the project, which carries it (ProjectMembership.timezone) and
 * which the server treats as the authority.
 *
 * These format only. Nothing here decides whether a delivery was on time:
 * that is the server's answer, computed from instants, and it was correct
 * throughout. This is the label, not the measurement.
 */

/** The device's own zone. The fallback, and never the default. */
export const deviceZone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
};

/* A project's timezone is a string a person typed into the settings screen,
   so it can be wrong. Intl throws on an unknown zone, and a page that throws
   while rendering a time is worse than a time in the wrong zone. */
const zoneCache = new Map<string, string>();

export function safeZone(timeZone: string | null | undefined): string {
    if (!timeZone) return deviceZone();
    const cached = zoneCache.get(timeZone);
    if (cached !== undefined) return cached;
    let resolved = timeZone;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone }).format(new Date());
    } catch {
        resolved = deviceZone();
    }
    zoneCache.set(timeZone, resolved);
    return resolved;
}

export type Formatter = (iso: string | null | undefined) => string;

/* Built once per zone and shape. A board with forty cards on it re-renders
   every fifteen seconds, and building an Intl.DateTimeFormat per card per
   render is work nobody asked for. */
const formatters = new Map<string, Formatter>();

function formatter(kind: string, timeZone: string, options: Intl.DateTimeFormatOptions, locale = 'en-US'): Formatter {
    const zone = safeZone(timeZone);
    const key = `${kind}|${zone}`;
    const cached = formatters.get(key);
    if (cached) return cached;

    const fmt = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone });
    const format: Formatter = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        /* An unparseable timestamp is shown as it arrived rather than as
           "Invalid Date": if something upstream is wrong, the raw value is
           what somebody needs to see to find out what. */
        return Number.isNaN(d.getTime()) ? iso : fmt.format(d);
    };
    formatters.set(key, format);
    return format;
}

/** 3:56 PM. For a deadline or an event on a day that is already on screen. */
export const clockFor = (timeZone: string): Formatter =>
    formatter('clock', timeZone, { hour: 'numeric', minute: '2-digit' });

/** Sep 14, 3:56 PM. For an event whose day is not otherwise obvious. */
export const stampFor = (timeZone: string): Formatter =>
    formatter('stamp', timeZone, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

/** Sep 14, 2026, 3:56 PM. For a record somebody may read a year later. */
export const momentFor = (timeZone: string): Formatter =>
    formatter('moment', timeZone, {
        year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });

/** 2026-09-14. A date carried on a document, in the contract's zone.
 *
 * `issuedAt.slice(0, 10)` is the tempting one-liner and it is UTC: an invoice
 * issued at 8pm in Chicago came out dated the following day, on the invoice
 * and on its PDF. Found by walking docs/day-rehearsal.md.
 *
 * en-CA because every other date in this application is YYYY-MM-DD: service
 * dates, billing periods, the import table. An invoice reading 09/14/2026
 * beside a period reading 2026-09-01 is two date formats on one card. */
export const dateFor = (timeZone: string): Formatter =>
    formatter('date', timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA');

/** A date in the project's zone, as YYYY-MM-DD. Defaults to now.
 *
 * A service date is a contract day, not a device day. Near midnight, or on a
 * phone left on the wrong zone, "today" from the browser is the wrong day and
 * a whole pharmacy list lands against it. en-CA is the short way to ask Intl
 * for an ISO-shaped date.
 *
 * The `at` argument is for ranges: the start of "the last thirty days" has to
 * be resolved in the same zone as its end, or the range is a day out at one
 * end for part of every evening. */
export function todayIn(timeZone: string, at: Date = new Date()): string {
    return at.toLocaleDateString('en-CA', { timeZone: safeZone(timeZone) });
}

/* ───────────────────────────────────── THE OTHER END OF THAT RANGE.
 *
 * The screens that open on "the last thirty days" -- the drivers record,
 * one driver's own record, the client's performance report -- each had
 * their own copy of this:
 *
 *     const d = new Date();
 *     d.setDate(d.getDate() - offsetDays);
 *     return d.toISOString().slice(0, 10);
 *
 * which subtracts days in the DEVICE's timezone and then reads the answer
 * in UTC. Two zones, so the answer moves with the time of day: open the
 * drivers record at nine in the evening in San Antonio and UTC has already
 * gone past midnight, so "thirty days back" is a different thirty days
 * from the one you got that morning. On the screen somebody is paid from,
 * a window that quietly shifts by a day is a figure that quietly changes.
 *
 * Subtracting from an instant is also wrong across a DST boundary, which is
 * how the same arithmetic lost a day in server/test/uh-client-import on
 * 29 October 2026: 1 November is twenty-five hours long in America/Chicago,
 * so thirty times 86,400,000 milliseconds does not land where the calendar
 * says it should.
 *
 * So: resolve today IN THE PROJECT'S ZONE, then step whole days through
 * Date.UTC, where every day is twenty-four hours and there is no DST to
 * cross. The result is a calendar date, which is what the server's `from`
 * and `to` parameters are.
 */
export function daysBeforeIn(timeZone: string, days: number, at: Date = new Date()): string {
    const [y, m, d] = todayIn(timeZone, at).split('-').map(Number);
    return new Date(Date.UTC(y!, m! - 1, d! - days)).toISOString().slice(0, 10);
}
